import RecordedChampion from '../../../components/RecordedChampion.jsx'
import { useMemo } from 'react'
import { prepWorklist, readinessSummary, CHECKS } from '../../../lib/championshipReadiness.js'
import { formatDate, sportMeta } from '../../../lib/championships.js'
import styles from './Readiness.module.css'

/**
 * READINESS — what is coming, and whether we are ready for it.
 *
 * Brian: "now you understand the challenge of crowning a champion but doing so
 * with preparation. its the reason this has to be done."
 *
 * ⚠️ THIS IS THE VIEW EVERY OTHER PIECE OF WORK WAS FEEDING. The dates, the
 * phantom retirements, the school × sport association, the assignment engine —
 * each one is a column here. Crowning a champion after the fact is not the job:
 * by the time a title is announced the school is already being called, and the
 * ring goes to whoever was READY.
 *
 * ⚠️ RANKED BY READINESS × TIME, NOT EITHER ALONE. A championship 400 days out
 * with nothing done is fine — there is a year. One in nine days with no owner
 * and no crest is an order about to be lost. Sorting on readiness alone buries
 * exactly the rows that can still be saved, which is how a preparation list
 * becomes a list nobody opens.
 *
 * ⚠️ RETIRED RECORDS ARE DROPPED, NOT RANKED LOW. Preparation is finite: an
 * hour spent on a Big Ten water polo title that does not exist is an hour not
 * spent on one that does. That is why the phantoms mattered — they consume the
 * exact resource this view allocates.
 */
export default function Readiness({ rows = [], now = new Date(), onOpen }) {
  const summary = useMemo(() => readinessSummary(rows, now), [rows, now])
  const work = useMemo(() => prepWorklist(rows, { now }), [rows, now])

  const gapLabel = (id) => CHECKS.find((c) => c.id === id)?.label || id

  return (
    <div className={styles.wrap}>
      <div className={styles.summary}>
        <div className={styles.stat}>
          <b>{summary.ready.toLocaleString()}</b>
          <span>ready to crown</span>
        </div>
        <div className={styles.stat}>
          <b data-bad={summary.notReady ? '1' : '0'}>{summary.notReady.toLocaleString()}</b>
          <span>not ready</span>
        </div>
        {/* THE NUMBER THAT ACTUALLY MATTERS — unprepared AND inside 30 days.
            Everything else is a backlog; this is a deadline. */}
        <div className={styles.stat} data-critical="1">
          <b data-bad={summary.critical ? '1' : '0'}>{summary.critical.toLocaleString()}</b>
          <span>unready inside 30 days</span>
        </div>
        <div className={styles.stat}>
          <b>{summary.retired.toLocaleString()}</b>
          <span>retired — cannot be crowned</span>
        </div>
      </div>

      {/* What is missing, across the board. A count per gap tells you which one
          fix buys the most readiness — rather than working record by record. */}
      {Object.keys(summary.byGap).length > 0 && (
        <div className={styles.gaps}>
          <span className={styles.gapsHead}>What is missing, most common first</span>
          {Object.entries(summary.byGap)
            .sort((a, b) => b[1] - a[1])
            .map(([id, n]) => (
              <span key={id} className={styles.gapChip}>
                {gapLabel(id)}<b>{n.toLocaleString()}</b>
              </span>
            ))}
        </div>
      )}

      <p className={styles.lead}>
        Ranked by how soon it lands against how much is still missing. A gap here is not a
        data complaint — it is the reason a specific order will be lost on a specific day.
      </p>

      <div className={styles.list}>
        {work.rows.map(({ champ, days, band, score, missing }) => {
          const sport = sportMeta(champ.sport)
          return (
            <button key={champ.id} type="button" className={styles.row} data-band={band}
              style={{ '--sport': sport.color }} onClick={() => onOpen?.(champ)}>
              <span className={styles.when} data-band={band}>
                {/* An undated record says so. It is not "someday" — it is the
                    most preparable thing on the list, because nobody can get
                    ready for a day nobody knows. */}
                {days === null ? 'No date' : days <= 0 ? 'now' : `${days}d`}
              </span>
              <span className={styles.main}>
                <span className={styles.name}>Championship: {champ.name}</span><RecordedChampion record={champ} now={now} />
                <span className={styles.meta}>
                  <span className={styles.dot} style={{ background: sport.color }} />
                  {champ.sport || 'Sport not set'}
                  {champ.effDate ? ` · ${formatDate(champ.effDate)}` : ''}
                </span>
                <span className={styles.needs}>
                  {missing.map((m) => (
                    <span key={m.id} className={styles.need} title={m.fix}>{m.label}</span>
                  ))}
                </span>
              </span>
              <span className={styles.score}>
                <b>{score}%</b><span>ready</span>
              </span>
            </button>
          )
        })}
      </div>

      {work.rows.length === 0 && (
        <p className={styles.muted}>
          {summary.applicable === 0
            ? 'No championships loaded yet.'
            : 'Everything upcoming is prepared — date, sport, governing body and an owner on each.'}
        </p>
      )}
      {work.total > work.rows.length && (
        <p className={styles.muted}>
          Showing the {work.rows.length.toLocaleString()} most urgent of {work.total.toLocaleString()}.
        </p>
      )}
    </div>
  )
}
